// Pantalla "Gestió" d'un Empleat concret: Horari, Absències, Baixes i
// Calendari combinat. Reutilitza Horari/Absències/Baixes/CH/Calendari/Resum
// sense canviar-ne la lògica; l'aspecte v2 el dóna el chrome d'aquesta
// pantalla i gestioempleat.css.

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import HorariEmpleat from "./HorariEmpleat";
import AbsenciesEmpleat from "./AbsenciesEmpleat";
import BaixesEmpleat from "./BaixesEmpleat";
import CalendariEmpleat from "./CalendariEmpleat";
import ControlHorariEmpleat from "./ControlHorariEmpleat";
import ResumEmpleat from "./ResumEmpleat";
import AjudaGestioEmpleat from "./AjudaGestioEmpleat";
import "../dashboard_v2.css";
import "./gestioempleat.css";

const SUBPESTANYES = [
  { id: "horari", label: "Horari" },
  { id: "absencies", label: "Absències" },
  { id: "baixes", label: "Baixes" },
  { id: "controlhorari", label: "Control Horari" },
  { id: "calendari", label: "Calendari" },
  { id: "resum", label: "Resum" },
];

function mateixId(a, b) {
  if (a == null || b == null || a === "") return false;
  return Number(a) === Number(b);
}

function GestioEmpleat({ empleatProp, onTornar, subnav, empleats, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { rolUsuari, empresaSeleccionada } = useUserContext();

  const empleat = empleatProp || location.state?.empleat || null;

  const [subpestanyaActiva, setSubpestanyaActiva] = useState("horari");
  const [editantHorari, setEditantHorari] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const canviarSubpestanya = (id) => {
    setSubpestanyaActiva(id);
    if (id !== "horari") setEditantHorari(false);
  };

  useEffect(() => {
    setEditantHorari(false);
  }, [empleat?.idEmpleats]);

  const tornar = () => {
    if (onTornar) onTornar();
    else navigate("/dashboard/empleats");
  };

  TeclaEscEnter(tornar, null);

  const potAccedir = tincPermis(rolUsuari, "ACCES", "EMPLEATS");

  const currentIndex = empleats
    ? empleats.findIndex((e) => mateixId(e.idEmpleats, empleat?.idEmpleats))
    : -1;
  const mostraNav = Boolean(empleats && empleats.length > 1 && currentIndex >= 0 && onNavigate);

  const anteriorEmpleat = () => {
    if (!mostraNav) return;
    const newIndex = currentIndex === 0 ? empleats.length - 1 : currentIndex - 1;
    onNavigate(empleats[newIndex]);
  };

  const seguentEmpleat = () => {
    if (!mostraNav) return;
    const newIndex = currentIndex === empleats.length - 1 ? 0 : currentIndex + 1;
    onNavigate(empleats[newIndex]);
  };

  if (!potAccedir) {
    return (
      <div className="dv2 dv2-entity gestioempleat">
        <p className="dv2-empty">No tens permisos per accedir a la Gestió laboral d'Empleats.</p>
      </div>
    );
  }

  if (!empleat) {
    return (
      <div className="dv2 dv2-entity gestioempleat">
        <p className="dv2-empty">No s'ha rebut cap empleat. Torna a la llista i selecciona'n un abans d'entrar a Gestió.</p>
        <div className="dv2-entity-dock">
          <div className="dv2-footerActions">
            <button type="button" className="dv2-btn" onClick={tornar}>
              Tornar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const nomComplet = [empleat.Nom, empleat.Cognom1, empleat.Cognom2].filter(Boolean).join(" ");

  const renderContingut = () => {
    switch (subpestanyaActiva) {
      case "horari":
        return (
          <>
            {!editantHorari && (
              <div className="gestioempleat-toolbar">
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => setEditantHorari(true)}>
                  Modificar horari
                </button>
              </div>
            )}
            <HorariEmpleat
              idEmpleat={empleat.idEmpleats}
              idEmpresa={empresaSeleccionada?.IdEmpresa}
              nomEmpleat={nomComplet}
              readOnly={!editantHorari}
              onTornar={editantHorari ? () => setEditantHorari(false) : undefined}
            />
          </>
        );
      case "absencies":
        return <AbsenciesEmpleat idEmpleat={empleat.idEmpleats} idEmpresa={empresaSeleccionada?.IdEmpresa} />;
      case "baixes":
        return <BaixesEmpleat idEmpleat={empleat.idEmpleats} idEmpresa={empresaSeleccionada?.IdEmpresa} />;
      case "controlhorari":
        return <ControlHorariEmpleat idEmpleat={empleat.idEmpleats} idEmpresa={empresaSeleccionada?.IdEmpresa} />;
      case "calendari":
        return (
          <CalendariEmpleat
            idEmpleat={empleat.idEmpleats}
            idEmpresa={empresaSeleccionada?.IdEmpresa}
            onNavegarPestanya={canviarSubpestanya}
          />
        );
      case "resum":
        return <ResumEmpleat idEmpleat={empleat.idEmpleats} idEmpresa={empresaSeleccionada?.IdEmpresa} />;
      default:
        return null;
    }
  };

  const esControlHorari = subpestanyaActiva === "controlhorari";

  return (
    <div className="dv2 dv2-entity gestioempleat">
      {mostraAjuda && (
        <AjudaGestioEmpleat pestanya={subpestanyaActiva} onTancar={() => setMostraAjuda(false)} />
      )}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className="dv2-pageHeader-icon" aria-hidden="true">
            👷
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">Empleats · Gestió</h1>
            <p className="dv2-pageHeader-subtitle">
              {nomComplet || "Dades laborals"}
              {empleat.Actiu === 0 ? " · Inactiu" : ""}
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title="Com funciona aquesta pantalla"
            aria-label="Ajuda"
            onClick={() => setMostraAjuda(true)}
          >
            ?
          </button>
          <button type="button" className="dv2-btn" onClick={tornar}>
            Tornar
          </button>
        </div>
      </div>
      {subnav}
      <nav className="dv2-subnav" aria-label="Seccions de gestió">
        {SUBPESTANYES.map((sp) => (
          <button
            key={sp.id}
            type="button"
            className={`dv2-subnav-btn ${subpestanyaActiva === sp.id ? "is-active" : ""}`}
            onClick={() => canviarSubpestanya(sp.id)}
          >
            {sp.label}
          </button>
        ))}
      </nav>
      <div className={`dv2-entity-body${esControlHorari ? " gestioempleat-content-flex" : ""}`}>
        {esControlHorari ? (
          renderContingut()
        ) : (
          <section className="dv2-card gestioempleat-panel">{renderContingut()}</section>
        )}
      </div>
      {mostraNav && (
        <div className="dv2-entity-dock">
          <div className="dv2-recordNav">
            <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anteriorEmpleat} title="Anterior">
              ‹
            </button>
            <span className="dv2-recordNav-info">
              {currentIndex + 1} / {empleats.length}
            </span>
            <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={seguentEmpleat} title="Següent">
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestioEmpleat;
